import { useQuery } from '@tanstack/react-query';
import { OperationsApi } from '@Apis';
import {
  FeedbackFilterStatus,
  FeedbackFilterCategory,
  FeedbackResultCategory,
  EuqTimeRangeEnum,
  ServiceTypeFilter,
} from '../../type';
import { useMemo } from 'react';

interface FilterCountsParams {
  orgId: string;
  timeRange: EuqTimeRangeEnum;
  enabled?: boolean;
}

export type RequestSourceKey =
  | 'Plugin'
  | 'Banner'
  | 'AutoCollect'
  | 'AdminReport';

interface FilterCounts {
  all: number;
  status: Record<FeedbackFilterStatus, number>;
  category: Record<FeedbackFilterCategory, number>;
  resultCategory: Record<FeedbackResultCategory, number>;
  serviceType: Record<ServiceTypeFilter, number>;
  requestSource: Record<RequestSourceKey, number>;
}

export const useFilterCounts = ({
  orgId,
  timeRange,
  enabled = true,
}: FilterCountsParams): FilterCounts | undefined => {
  // Query for category statistics (reported type: spam, phishing, notspam)
  const { data: categoryData, isLoading: categoryLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'category', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'category',
      }),
    select: (data) => data.data,
    enabled: enabled && !!orgId,
    staleTime: 30000,
  });

  // Query for status statistics (approval status: created, approved, rejected, bypassed)
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'status', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'status',
      }),
    select: (data) => data.data,
    enabled: enabled && !!orgId,
    staleTime: 30000,
  });

  // Query for analysis result statistics (result_category: spam, threat/phishing, flagged)
  const { data: resultData, isLoading: resultLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'result_category', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'result_category',
      }),
    select: (data) => data.data,
    enabled: enabled && !!orgId,
    staleTime: 30000,
  });

  // Query for service type statistics
  const { data: serviceTypeData, isLoading: serviceTypeLoading } = useQuery({
    queryKey: ['euqFeedbackStatistic', 'service_type', timeRange, orgId],
    queryFn: () =>
      OperationsApi.apiGetEuqFeedbackStatistic({
        predefined: timeRange,
        dimension: 'service_type',
      }),
    select: (data) => data.data,
    enabled: enabled && !!orgId,
    staleTime: 30000,
  });

  // Query for request_source statistics (collect source)
  const { data: requestSourceData, isLoading: requestSourceLoading } = useQuery(
    {
      queryKey: ['euqFeedbackStatistic', 'request_source', timeRange, orgId],
      queryFn: () =>
        OperationsApi.apiGetEuqFeedbackStatistic({
          predefined: timeRange,
          dimension: 'request_source',
        }),
      select: (data) => data.data,
      enabled: enabled && !!orgId,
      staleTime: 30000,
    }
  );

  const counts = useMemo(() => {
    const isLoading =
      categoryLoading ||
      statusLoading ||
      resultLoading ||
      serviceTypeLoading ||
      requestSourceLoading;

    if (
      isLoading ||
      (!categoryData &&
        !statusData &&
        !resultData &&
        !serviceTypeData &&
        !requestSourceData)
    ) {
      return undefined;
    }

    // Helper function to calculate total from API data array
    const calculateTotal = (dataArray: any[]) => {
      if (!dataArray || !Array.isArray(dataArray)) return 0;
      return dataArray.reduce((sum, item) => sum + (item.count || 0), 0);
    };

    // Process category statistics (reported type)
    const spamTotal = calculateTotal(categoryData?.spam || []);
    const phishingTotal = calculateTotal(categoryData?.phishing || []);
    const notspamTotal = calculateTotal(categoryData?.notspam || []);

    const categoryCounts: Record<FeedbackFilterCategory, number> = {
      [FeedbackFilterCategory.All]: 0, // Not used but included for completeness
      [FeedbackFilterCategory.Spam]: spamTotal,
      [FeedbackFilterCategory.Phishing]: phishingTotal,
      [FeedbackFilterCategory.NotSpam]: notspamTotal,
    };

    // Process status statistics (approval status)
    const createdTotal = calculateTotal(statusData?.created || []);
    const approvedTotal = calculateTotal(statusData?.approved || []);
    const rejectedTotal = calculateTotal(statusData?.rejected || []);
    const bypassedTotal = calculateTotal(statusData?.bypassed || []);

    const statusCounts: Record<FeedbackFilterStatus, number> = {
      [FeedbackFilterStatus.All]: 0, // Not used but included for completeness
      [FeedbackFilterStatus.Created]: createdTotal,
      [FeedbackFilterStatus.Approved]: approvedTotal,
      [FeedbackFilterStatus.Rejected]: rejectedTotal,
      [FeedbackFilterStatus.Bypassed]: bypassedTotal,
    };

    // Process analysis result statistics
    const spamResultTotal = calculateTotal(resultData?.spam || []);
    const threatResultTotal = calculateTotal(resultData?.threat || []); // threat = phishing in analysis results
    const flaggedResultTotal = calculateTotal(resultData?.flagged || []);
    const normalResultTotal = calculateTotal(resultData?.normal || []);
    const inProgressResultTotal = calculateTotal(resultData?.in_progress || []);
    const errorResultTotal = calculateTotal(resultData?.error || []);

    const resultCategoryCounts: Record<FeedbackResultCategory, number> = {
      [FeedbackResultCategory.All]: 0, // Not used but included for completeness
      [FeedbackResultCategory.Spam]: spamResultTotal,
      [FeedbackResultCategory.Phishing]: threatResultTotal, // threat maps to phishing
      [FeedbackResultCategory.Flagged]: flaggedResultTotal,
      [FeedbackResultCategory.Normal]: normalResultTotal,
      [FeedbackResultCategory.InProgress]: inProgressResultTotal,
      [FeedbackResultCategory.Error]: errorResultTotal,
    };

    // Process service type statistics
    const outlookTotal = calculateTotal(serviceTypeData?.outlook || []);
    const gmailTotal = calculateTotal(serviceTypeData?.gmail || []);

    const serviceTypeCounts: Record<ServiceTypeFilter, number> = {
      [ServiceTypeFilter.All]: 0,
      [ServiceTypeFilter.Outlook]: outlookTotal,
      [ServiceTypeFilter.Gmail]: gmailTotal,
    };

    // Process request_source statistics
    const pluginRequestSourceTotal = calculateTotal(
      requestSourceData?.Plugin || []
    );
    const bannerRequestSourceTotal = calculateTotal(
      requestSourceData?.Banner || []
    );
    const autoCollectRequestSourceTotal = calculateTotal(
      requestSourceData?.AutoCollect || []
    );

    const requestSourceCounts: Record<RequestSourceKey, number> = {
      Plugin: pluginRequestSourceTotal,
      Banner: bannerRequestSourceTotal,
      AutoCollect: autoCollectRequestSourceTotal,
      AdminReport: calculateTotal(requestSourceData?.CentralOperation || []),
    };

    // Calculate total from all categories (use category data as the primary source for total count)
    const totalCount = spamTotal + phishingTotal + notspamTotal || 0;

    return {
      all: totalCount,
      status: statusCounts,
      category: categoryCounts,
      resultCategory: resultCategoryCounts,
      serviceType: serviceTypeCounts,
      requestSource: requestSourceCounts,
    };
  }, [
    categoryData,
    statusData,
    resultData,
    serviceTypeData,
    requestSourceData,
    categoryLoading,
    statusLoading,
    resultLoading,
    serviceTypeLoading,
    requestSourceLoading,
  ]);

  return counts;
};
